import { memo, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  Typography,
  Button,
  Collapse,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  debtAmountQuery,
  IDebtAmountInfoPayload,
} from "../../services/appolo/paymentQuery";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { paymentMutation } from "../../services/appolo/Mutations/paymentMutation";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { paymetnFields } from "../../constants/paymentFields";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

import PaymentHeader from "../../components/PaymentComponents/PaymentHeader";
import AllPaidOffBox from "../../components/PaymentComponents/PaymentPaidOff";
import { SupplierGroupsList } from "../../components/PaymentComponents/SupplierGroupsList";
import ServiceUnavailable from "../../components/PaymentComponents/ServiceUnavailable";

const useStyles = makeStyles(() => ({
  page: {
    padding: 20,
    display: "flex",
    justifyContent: "center",
    margin: "auto",
    alignItems: "center",
    minHeight: "90vh",
  },
  descrippt: {
    fontSize: 18,
    fontWeight: 500,
    "@media (max-width: 550px)": {
      textAlign: "center",
    },
  },
  finalCount: {
    fontFamily: "Montserrat",
    fontSize: 16,
    fontWeight: 500,
    marginBottom: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    maxWidth: 500,
    width: "100%",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
  },
  section: {
    marginBottom: 24,
  },
  groupTitle: {
    fontWeight: 500,
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  itemLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  labelPurple: {
    color: "#4551D1",
    fontSize: 15,
    fontWeight: 500,
  },

  description: {
    fontSize: 13,
    fontWeight: 500,
    color: "#484848",
  },

  itemBox: {
    border: "1px solid #5664F5",
    borderRadius: "10px",
    padding: "6px 12px 3px 12px",
    marginBottom: "12px",
  },

  button: {
    backgroundColor: "#5664F5",
    height: "40px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: "10px",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#4352d6",
    },
    fontFamily: "Montserrat",
  },
  checkbox: {
    color: "#5664F5",
    "&.Mui-checked": {
      color: "#5664F5",
    },
    transform: "scale(1.4)",
    padding: "4px",
  },
  labelContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    gap: "12px",

    "@media (max-width: 425px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "4px",
    },
  },
  labelText: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    marginTop: "10px",
    marginRight: "10px",
  },
  amount: {
    fontWeight: 500,
    fontSize: 16,
    whiteSpace: "nowrap",
    marginTop: "15px",

    "@media (max-width: 425px)": {
      marginTop: "8px",
    },
  },

  retryButton: {
    textTransform: "none",
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 9999,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "all",
  },
  loadingTitle: {
    marginTop: "16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#000",
  },

  loadingSubtitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    marginTop: "4px",
  },

  loadingInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  sectionWrapper: {
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    padding: "16px",
    background: "#fafafa",
    marginBottom: "16px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },

  totalAmountChip: {
    backgroundColor: "#5554f3",
    borderRadius: "8px",
    padding: "2px 8px",
    fontSize: "1rem",
    fontWeight: 500,
    color: "#fff",
    transform: "translateY(-1px)",
    display: "inline-block",
  },
  infoCard: {
    borderRadius: "16px",
    border: "1px solid #d1d9ff",
    padding: "32px 24px",
    backgroundColor: "#f5f6fe",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
    boxShadow: "0 4px 12px rgba(85, 84, 243, 0.1)",
  },
  infoTitle: {
    fontWeight: 700,
    marginTop: "8px",
    fontSize: "1.2rem",
    color: "#333",
    textAlign: "center",
    fontFamily: "Montserrat",
  },
  infoSubtitle: {
    marginTop: "3px",
    color: "#666",
    fontSize: "1rem",
    textAlign: "center",
    fontFamily: "Montserrat",
  },
}));

type SupplierItem = {
  supplierName: string;
  supplierGuid: string;
  collapsed: boolean;
  items: {
    key: string;
    label: string;
    description: string;
    value: number;
    checked: boolean;
  }[];
};

const PaymentPage = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const getRegionLogo = (org: string | null): string => {
    switch (org || "") {
      case 'ООО "ЮРИЦ"':
        return "../../../../uric.png";
      case 'АО "ЕРИЦ ЯНАО"':
        return "../../../../eric2.png";
      case 'АО "РИЦ"':
        return "../../../../ric2.png";
      case "АО «ЭК «Восток»":
      default:
        return "../../../../vostok.png";
    }
  };

  const orgParam = params.get("org");
  const addrParam = params.get("addr");
  const regionName = getRegionLogo(orgParam);

  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [payError, setPayError] = useState<boolean>(false);
  const [payWaiting, setPayWaiting] = useState<boolean>(false);
  const [allPaidOff, setAllPaidOff] = useState<boolean>(false);
  const [isDebtAmountInfoOld, setIsDebtAmountInfoOld] = useState<boolean>(true);
  const [supplierGroups, setSupplierGroups] = useState<SupplierItem[]>([]);
  const [openedGroupIndex, setOpenedGroupIndex] = useState<number | null>(null);
  const [paymentRequested, setPaymentRequested] = useState<boolean>(false);

  const formatCurrency = (value: number): string =>
    value.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const toggleItem = (supplierIndex: number, itemIndex: number) => {
    setSupplierGroups((prevGroups) =>
      prevGroups.map((group, sIdx) => {
        if (sIdx !== supplierIndex) return group;

        return {
          ...group,
          items: group.items.map((item, iIdx) =>
            iIdx === itemIndex ? { ...item, checked: !item.checked } : item
          ),
        };
      })
    );
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const [
    fetchPayment,
    { data: payData, error: payQueryError, loading: payLoading },
  ] = useLazyQuery(debtAmountQuery().query, {
    onError: () => {
      setPayError(true);
      setLoading(false);
    },
  });

  const [sendPayment, { data: sendData, loading: sendLoading }] =
    useMutation(paymentMutation);

  const HandlePay = () => {
    setPaymentRequested(true);
    setPayWaiting(true);

    const debtsPaymentInfo = supplierGroups
      .map((group) => {
        const selectedItems = group.items.reduce(
          (acc, item) => {
            acc[item.key] = item.checked ? item.value.toFixed(4) : "0";
            return acc;
          },
          {} as Record<string, string>
        );

        const hasChecked = group.items.some((item) => item.checked);

        if (!hasChecked) return null;

        return {
          supplierGuid: group.supplierGuid,
          sumRestOfBasicDebt: selectedItems["sumRestOfBasicDebt"] ?? "0",
          stateFee: selectedItems["stateFee"] ?? "0",
          sumFine: selectedItems["sumFine"] ?? "0",
          eePostCost: selectedItems["eePostCost"] ?? "0",
          extraExpensesWithoutPostCost: selectedItems["extraExpensesWithoutPostCost"] ?? "0",
        };
      })
      .filter((item) => item !== null);

    sendPayment({
      variables: {
        paymentId: payData.getDebtAmountInfo.paymentId,
        debtsPaymentInfo: {
          debtsPaymentInfo: debtsPaymentInfo,
        },
      },
    });
    console.log(JSON.stringify(debtsPaymentInfo, null, 2));
  };

    useEffect(() => {
    if (!paymentRequested) return;
    if (sendLoading) return;
    if (sendData?.getPaymentLink) {
      window.location.href = sendData.getPaymentLink;
    } else {
      const orgQueryParam = orgParam
        ? `?org=${encodeURIComponent(orgParam)}`
        : "";
      window.location.href = "/FaultPage" + orgQueryParam;
    }
  }, [sendData, sendLoading, paymentRequested]);

  const loadPaymentInfo = (payload: IDebtAmountInfoPayload) => {
    setPayError(false);
    setLoading(true);
    fetchPayment({
      variables: payload,
    });
  };

  useEffect(() => {
    const payload: IDebtAmountInfoPayload = {
      acn: params.get("acn") ?? "",
      fine: parseFloat(params.get("fine") ?? "0"),
      eeResumption: parseFloat(params.get("eeres") ?? "0"),
      eeRepresentation: parseFloat(params.get("eerep") ?? "0"),
      eeReference: parseFloat(params.get("eeref") ?? "0"),
      eePostCost: parseFloat(params.get("eepos") ?? "0"),
      eeHalting: parseFloat(params.get("eehal") ?? "0"),
      stateFee: parseFloat(params.get("stfe") ?? "0"),
      restOfBasicDebt: parseFloat(params.get("robd") ?? "0"),
      qrd: params.get("qrd") ?? new Date().toISOString().split("T")[0],
    };

    loadPaymentInfo(payload);
  }, []);

  useEffect(() => {
    if (payQueryError || !payData?.getDebtAmountInfo) {
      setPayError(true);
      setLoading(false);
      return;
    }

    setPayError(false);
    setLoading(false);

    const info = payData.getDebtAmountInfo;

    const groups: SupplierItem[] = info.debtsAmountInfo.map((supplier: any) => {
      const items = paymetnFields
        .map((field) => {
          const value = parseFloat(supplier[field.key]) || 0;
          return {
            key: field.key,
            label: field.label,
            description: field.description,
            value,
            checked: value > 0,
          };
        })
        .filter((item) => item.value > 0);

      return {
        supplierName: supplier.supplierName,
        supplierGuid: supplier.supplierGuid,
        collapsed: false,
        items,
      };
    });

    setSupplierGroups(groups);

    const totalDebt = groups
      .flatMap((g) => g.items)
      .reduce((sum, item) => sum + item.value, 0);

    setAllPaidOff(totalDebt === 0);

    setIsDebtAmountInfoOld(payData?.getDebtAmountInfo?.isDebtAmountInfoOld);
  }, [payLoading, payQueryError, payData]);

  const totalAmount = supplierGroups
    .flatMap((g) => g.items)
    .reduce((sum, item) => (item.checked ? sum + item.value : sum), 0);

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        {payWaiting && (
          <div className={classes.loadingOverlay}>
            <div className={classes.loadingInner}>
              <div className="spinner"></div>
              {/* <div className={classes.loadingTitle}>Обработка платежа</div> */}
              {/* <div className={classes.loadingSubtitle}>
                Ждём подтверждения от банка...
              </div> */}
            </div>
          </div>
        )}

        {loading || payLoading ? (
          <div className="loading-wrapper">
            <div className="spinner"></div>
            <div className="loading-title">Создание счёта</div>
            <div className="loading-subtitle">Данные загружаются</div>
          </div>
        ) : payError || payQueryError ? (
          <ServiceUnavailable orgParam={orgParam} handleRetry={handleRetry} />
        ) : (
          <>
            <div className={classes.descrippt}>Оплата задолженности</div>

            <PaymentHeader
              accountNumber={payData?.getDebtAmountInfo?.accountNumber || ""}
              addr={addrParam || ""}
              debtInfoDate={payData?.getDebtAmountInfo?.debtInfoDate}
              regionName={regionName}
            />

            <Divider style={{ margin: "8px 0 20px 0" }} />

            {allPaidOff ? (
              <AllPaidOffBox />
            ) : (
              <>
                {isDebtAmountInfoOld ? (
                  <div>
                    <Box className={classes.infoCard}>
                      <HourglassTopIcon
                        sx={{ fontSize: 44, color: "#5664F5" }}
                      />
                      <Typography variant="h6" className={classes.infoTitle}>
                        Информация о задолженности устарела
                      </Typography>
                      <Typography className={classes.infoSubtitle}>
                        Пожалуйста, зайдите позже — актуальные данные скоро
                        появятся.
                      </Typography>
                    </Box>
                  </div>
                ) : null}

                <SupplierGroupsList
                  supplierGroups={supplierGroups}
                  openedGroupIndex={openedGroupIndex}
                  setOpenedGroupIndex={setOpenedGroupIndex}
                  toggleItem={toggleItem}
                  classes={classes}
                />
              </>
            )}

            <>
              <Divider style={{ margin: "20px 0 0 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                }}
              >
                <Typography className={classes.finalCount}>К оплате</Typography>
                <Typography className={classes.finalCount}>
                  {!allPaidOff && !isDebtAmountInfoOld
                    ? `${formatCurrency(totalAmount)} ₽`
                    : "0,00 ₽"}
                </Typography>
              </div>
              <Button
                variant="contained"
                disabled={allPaidOff || isDebtAmountInfoOld || totalAmount == 0}
                fullWidth
                className={classes.button}
                onClick={HandlePay}
              >
                Оплатить
              </Button>
            </>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(PaymentPage);
